import { getApiBaseUrl } from './api';

export type MobilePassPlatform = 'apple' | 'google';
export type MobilePassKind = 'reservation' | 'gift_card' | 'ledger';

function getBookingAuthToken(): string | null {
  try {
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      if (session.expiresAt && Date.now() <= session.expiresAt) {
        return session.token || null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function reservationMobilePassUrl(
  reservationId: string,
  platform: MobilePassPlatform,
  accessToken: string,
): string {
  const base = getApiBaseUrl();
  const enc = encodeURIComponent(reservationId);
  return `${base}/mobile-passes/reservations/${enc}/${platform}?accessToken=${encodeURIComponent(accessToken)}`;
}

export function customerMobilePassUrl(
  kind: Exclude<MobilePassKind, 'reservation'>,
  resourceId: string,
  platform: MobilePassPlatform,
  accessToken?: string,
): string {
  const base = getApiBaseUrl();
  const enc = encodeURIComponent(resourceId);
  const segment = kind === 'gift_card' ? 'gift-cards' : 'ledgers';
  const url = `${base}/mobile-passes/${segment}/${enc}/${platform}`;
  if (!accessToken) return url;
  return `${url}?accessToken=${encodeURIComponent(accessToken)}`;
}

export async function fetchReservationMobilePassUrls(
  reservationId: string,
): Promise<{ apple: string; google: string } | null> {
  const token = getBookingAuthToken();
  if (!token) return null;
  const base = getApiBaseUrl();
  const enc = encodeURIComponent(reservationId);
  const response = await fetch(`${base}/mobile-passes/reservations/${enc}/signed-urls`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) return null;
  return response.json();
}

async function fetchCustomerMobilePassUrls(
  kind: Exclude<MobilePassKind, 'reservation'>,
  resourceId: string,
  token: string,
): Promise<{ apple: string; google: string }> {
  const base = getApiBaseUrl();
  const enc = encodeURIComponent(resourceId);
  const segment = kind === 'gift_card' ? 'gift-cards' : 'ledgers';
  const response = await fetch(`${base}/mobile-passes/${segment}/${enc}/signed-urls`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(await parsePassError(response, 'Could not open wallet pass'));
  }
  return response.json();
}

async function parsePassError(response: Response, fallback: string): Promise<string> {
  try {
    const parsed = (await response.json()) as { error?: string; message?: string };
    return parsed.error || parsed.message || fallback;
  } catch {
    return fallback;
  }
}

/** Resolve a signed pass path/URL against the booking app's current API base (LAN-safe). */
export function resolveMobilePassClientUrl(pathOrUrl: string): string {
  const clientBase = getApiBaseUrl().replace(/\/+$/, '');

  if (pathOrUrl.startsWith('/')) {
    return `${clientBase}${pathOrUrl}`;
  }

  try {
    const parsed = new URL(pathOrUrl);
    const suffix = parsed.pathname.replace(/^\/api\/v1/i, '') + parsed.search;
    return `${clientBase}${suffix}`;
  } catch {
    return pathOrUrl;
  }
}

/** Prefetch a signed Apple pass URL — iOS Safari requires a direct link tap, not async navigation. */
export async function prefetchCustomerApplePassUrl(
  kind: Exclude<MobilePassKind, 'reservation'>,
  resourceId: string,
): Promise<string | null> {
  const token = getBookingAuthToken();
  if (!token) return null;
  try {
    const urls = await fetchCustomerMobilePassUrls(kind, resourceId, token);
    return resolveMobilePassClientUrl(urls.apple);
  } catch {
    return null;
  }
}

/** Same synchronous navigation reservations use — required for iOS Safari. */
export function openCustomerMobilePassApple(
  kind: Exclude<MobilePassKind, 'reservation'>,
  resourceId: string,
  accessToken: string,
): void {
  window.location.assign(customerMobilePassUrl(kind, resourceId, 'apple', accessToken));
}

/** Safari/iOS must navigate directly to .pkpass URLs — blob downloads fail with "can't download this file". */
function openApplePassUrl(url: string): void {
  window.location.assign(url);
}

/** Gift card and house-account passes require customer JWT. */
export async function openCustomerMobilePass(
  kind: Exclude<MobilePassKind, 'reservation'>,
  resourceId: string,
  platform: MobilePassPlatform,
): Promise<void> {
  const token = getBookingAuthToken();
  if (!token) {
    throw new Error('Sign in to add passes to your wallet.');
  }

  const urls = await fetchCustomerMobilePassUrls(kind, resourceId, token);

  if (platform === 'apple') {
    openApplePassUrl(resolveMobilePassClientUrl(urls.apple));
    return;
  }

  const fetchUrl = `${resolveMobilePassClientUrl(urls.google)}?format=json`;
  const response = await fetch(fetchUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(await parsePassError(response, 'Could not open Google Wallet pass'));
  }
  const payload = (await response.json()) as { saveUrl?: string };
  if (!payload.saveUrl) {
    throw new Error('Google Wallet pass is not available yet.');
  }
  window.open(payload.saveUrl, '_blank', 'noopener,noreferrer');
}

export function openReservationMobilePass(
  reservationId: string,
  platform: MobilePassPlatform,
  accessToken?: string,
): void {
  if (platform === 'apple' && accessToken) {
    openApplePassUrl(reservationMobilePassUrl(reservationId, platform, accessToken));
    return;
  }

  if (accessToken) {
    window.open(reservationMobilePassUrl(reservationId, platform, accessToken), '_blank', 'noopener,noreferrer');
    return;
  }

  void fetchReservationMobilePassUrls(reservationId).then((urls) => {
    if (!urls) return;
    if (platform === 'apple') {
      openApplePassUrl(resolveMobilePassClientUrl(urls.apple));
      return;
    }
    window.open(resolveMobilePassClientUrl(urls[platform]), '_blank', 'noopener,noreferrer');
  });
}

export function openGiftCardMobilePass(giftCardId: string, platform: MobilePassPlatform): void {
  void openCustomerMobilePass('gift_card', giftCardId, platform);
}

export function openLedgerMobilePass(ledgerId: string, platform: MobilePassPlatform): void {
  void openCustomerMobilePass('ledger', ledgerId, platform);
}
